/* eslint-disable @typescript-eslint/no-unused-vars */
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { Product } from '../../integrations/woocommerce/types';
import {
  addWishlistItemOnServer,
  fetchWishlistFromServer,
  removeWishlistItemOnServer,
} from '../../api/woocommerce/wishlist.api';
import { loginSuccess, logout } from '../auth';
import { RootState } from '../rootReducer';
import {
  initializeWishlistRequest,
  setWishlistItems,
  toggleWishlistRequest,
  wishlistRequestFailure,
} from './wishlist.slice';

const WISHLIST_STORAGE_KEY = 'wishlist';

const loadWishlistFromLocalStorage = (): Product[] => {
  if (typeof window === 'undefined') return [];
  const persisted = window.localStorage.getItem(WISHLIST_STORAGE_KEY);
  if (!persisted) return [];
  try {
    const parsed = JSON.parse(persisted);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persistWishlistToLocalStorage = (items: Product[]): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

function* loadWishlistByAuthState(): SagaIterator {
  const auth: RootState['auth'] = yield select((state: RootState) => state.auth);
  const localItems = loadWishlistFromLocalStorage();

  yield put(setWishlistItems(localItems));

  if (auth.isAuthenticated && auth.token && auth.token !== 'null') {
    try {
      const serverItems: Product[] = yield call(fetchWishlistFromServer, auth.token);
      
      if (Array.isArray(serverItems) && serverItems.length > 0) {
        persistWishlistToLocalStorage(serverItems);
        yield put(setWishlistItems(serverItems));
      }
    } catch (error: unknown) {
      console.error("Sync error:", error);
    }
  }
}

function* handleInitializeWishlist(): SagaIterator {
  yield call(loadWishlistByAuthState);
}

function* handleAuthStateChanged(): SagaIterator {
  yield call(loadWishlistByAuthState);
}

function* handleToggleWishlist(action: PayloadAction<Product>): SagaIterator {
  const auth: RootState['auth'] = yield select((state: RootState) => state.auth);
  const wishlistItems: Product[] = yield select((state: RootState) => state.wishlist.items);
  
  const alreadyInWishlist = wishlistItems.some((item) => item.id === action.payload.id);
  
  try {
    let serverItems: Product[];
    if (auth.isAuthenticated && auth.token) {
      serverItems = alreadyInWishlist
        ? yield call(removeWishlistItemOnServer, action.payload.id, auth.token)
        : yield call(addWishlistItemOnServer, action.payload, auth.token);
    } else {
     
      serverItems = alreadyInWishlist
        ? wishlistItems.filter((item) => item.id !== action.payload.id)
        : [...wishlistItems, action.payload];
    }

    if (Array.isArray(serverItems)) {
      yield put(setWishlistItems(serverItems)); 
      persistWishlistToLocalStorage(serverItems);
    }
  } catch (error: unknown) {
    yield put(wishlistRequestFailure(getErrorMessage(error, 'Sync failed')));
  }
}

export function* watchWishlistActions(): SagaIterator {
  yield takeLatest(initializeWishlistRequest.type, handleInitializeWishlist);
  yield takeLatest(toggleWishlistRequest.type, handleToggleWishlist);
  yield takeLatest(loginSuccess.type, handleAuthStateChanged);
  yield takeLatest(logout.type, handleAuthStateChanged);
}